import { concurrency, JsonApiRequestError, Mutation, Query } from '@farfetched/core';
import { notifications } from '@leprecoins/effector-notifications';
import { createInstance } from '@leprecoins/effector-query';
import type { OverloadedParameters } from '@leprecoins/utility-types';
import { combine, sample } from 'effector';
import { format } from 'patronum';
import { API_URL } from 'shared/config';
import { $accessToken } from '../auth';

export const [createBaseQuery, createBaseMutation] = createInstance({
	url: API_URL,
	headers: combine({
		Authorization: format`Bearer ${$accessToken}`,
	}),
	credentials: 'include',
});

export const withNotifications = (
	operation:
		| Query<void, unknown, JsonApiRequestError>
		| Mutation<void, unknown, JsonApiRequestError>,
) => {
	sample({
		clock: operation.finished.failure,
		fn: ({ error }) => {
			const message = `${error.errorType}: ${error.explanation}`;

			console.error(message, error);
		},
	});

	sample({
		clock: operation.finished.failure.map(({ error }) => error),
		filter: withErrorMessage,
		fn: (error) => error.response.message,
		target: notifications.showError,
	});
};

export const createQuery: typeof createBaseQuery = (
	config: OverloadedParameters<typeof createBaseQuery>[0],
) => {
	const query = createBaseQuery(config);

	concurrency(query, {
		strategy: 'TAKE_LATEST',
	});

	withNotifications(query);

	return query;
};

export const createMutation: typeof createBaseMutation = (
	config: OverloadedParameters<typeof createBaseMutation>[0],
) => {
	const mutation = createBaseMutation(config);

	withNotifications(mutation);

	return mutation;
};

function withErrorMessage(
	error: JsonApiRequestError,
): error is JsonApiRequestError & { response: { message: string } } {
	return (
		'response' in error &&
		typeof error.response === 'object' &&
		error.response !== null &&
		'message' in error.response &&
		typeof error.response.message === 'string'
	);
}
